import { TFunction } from 'i18next';
import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const NEDARIM_ORIGIN = 'https://matara.pro';

/**
 * Nedarim document `language=en` for an English iframe, laid out left to right; with no
 * parameter the fields come back in Hebrew, which is the default we want. `language=he` was
 * pinned in the URL, so an English-language user got an English page with a Hebrew payment
 * form inside it - and `he` is not a value Nedarim document at all.
 */
function buildIframeUrl(language: string): string {
  const isEnglish = language.toLowerCase().startsWith('en');
  return `${NEDARIM_ORIGIN}/nedarimplus/iframe${isEnglish ? '?language=en' : ''}`;
}

const NEDARIM_ERROR_MAP: Record<string, string> = {
  'לא ניתן לבצע עסקה בדולרים במוסד זה': 'donations.nedarimErrors.noDollarTransactions',
  'לא הוקלד תוקף.': 'donations.nedarimErrors.noExpiry',
  'מבנה תוקף לא תקין.': 'donations.nedarimErrors.invalidExpiryFormat',
  'חודש או שנת תוקף שגויים.': 'donations.nedarimErrors.wrongExpiryDate',
  'פג תוקף.': 'donations.nedarimErrors.expired',
  'מספר כרטיס אשראי שגוי. טעות בהקלדה.': 'donations.nedarimErrors.invalidCardNumber',
  'CVV ERROR': 'donations.nedarimErrors.cvvError',
  'CVV לא תקין - יש להזין 3 ספרות': 'donations.nedarimErrors.invalidCvv',
  'NEED ZEOUT': 'donations.nedarimErrors.needId',
  'NEED CVV': 'donations.nedarimErrors.needCvv',
  'מספר כרטיס אשראי שגוי. (ריק)': 'donations.nedarimErrors.emptyCardNumber',
};

export function translateNedarimError(message: string, t: TFunction): string {
  const key = NEDARIM_ERROR_MAP[message];
  if (key) return t(key);

  if (message.startsWith('כרטיס זה חסום לסליקה')) return t('donations.nedarimErrors.cardBlocked');
  if (message.startsWith('מוסד לא פעיל')) return t('donations.nedarimErrors.institutionInactive');

  return message;
}

export const NEDARIM_PAYMENT_TYPE_STANDING_ORDER = 'HK';
export const NEDARIM_CURRENCY_ILS = '1';

export interface NedarimPaymentData {
  Mosad: string;
  ApiValid: string;
  PaymentType: 'HK';
  Currency: string;
  Zeout: string;
  FirstName: string;
  LastName: string;
  Street: string;
  City: string;
  Phone: string;
  Mail: string;
  Amount: string;
  Tashlumim: string;
  Day: string;
  Groupe: string;
  Comment: string;
  Param1: string;
  Param2: string;
  CallBack: string;
  CallBackMailError: string;
  ThirdPartyReceipt: string;
}

export interface TransactionResponse {
  Status: string;
  Message?: string;
}

type IframeStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

export function useNedarimIframe() {
  const { i18n } = useTranslation();
  const iframeUrl = useMemo(() => buildIframeUrl(i18n.language), [i18n.language]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [status, setStatus] = useState<IframeStatus>('loading');
  const [iframeHeight, setIframeHeight] = useState(0);
  const [transactionResponse, setTransactionResponse] = useState<TransactionResponse | null>(null);
  const [iframeKey, setIframeKey] = useState(0);

  // Addressed to Nedarim rather than to '*'. With a wildcard the card details and the mosad
  // credentials in this message are readable by whatever document happens to be in the frame.
  const postToIframe = useCallback((data: object) => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(data, NEDARIM_ORIGIN);
    }
  }, []);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Any page could post a TransactionResponse and be believed - the screen would show a
      // standing order that was never created.
      if (event.origin !== NEDARIM_ORIGIN) return;
      if (!event.data?.Name) return;

      switch (event.data.Name) {
        case 'Height':
          setIframeHeight(parseInt(event.data.Value) + 40);
          setStatus('ready');
          break;

        case 'TransactionResponse': {
          const response = event.data.Value as TransactionResponse;
          setTransactionResponse(response);
          // Success has to be stated, not merely "not an error": a status Nedarim adds later
          // would otherwise be read here as a completed transaction.
          setStatus(response.Status === 'OK' ? 'success' : 'error');
          postToIframe({ Name: 'GetHeight' });
          break;
        }
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const requestHeight = useCallback(() => {
    postToIframe({ Name: 'GetHeight' });
  }, [postToIframe]);

  const handleIframeLoad = useCallback(() => {
    requestHeight();
  }, [requestHeight]);

  useEffect(() => {
    window.addEventListener('resize', requestHeight);
    return () => window.removeEventListener('resize', requestHeight);
  }, [requestHeight]);

  const submitPayment = useCallback((data: NedarimPaymentData) => {
    setStatus('submitting');
    setTransactionResponse(null);
    postToIframe({
      Name: 'FinishTransaction2',
      Value: data,
    });
  }, [postToIframe]);

  /**
   * Bumping the key remounts the iframe, which is the point: after a successful charge the
   * frame still held the card that was just charged, so "create another" could send the same
   * card again for a different donor.
   */
  const reset = useCallback(() => {
    setStatus('loading');
    setIframeHeight(0);
    setTransactionResponse(null);
    setIframeKey((key) => key + 1);
  }, []);

  return {
    iframeRef,
    iframeKey,
    iframeUrl,
    iframeHeight,
    status,
    transactionResponse,
    handleIframeLoad,
    submitPayment,
    reset,
  };
}
